import React, { useState } from 'react';
import { Box, Typography, Button, Link as MuiLink } from '@mui/material';
import { styled } from '@mui/material/styles';
import Provider from 'provider';
import WhatWentWrong from './WhatWentWrong';
import { GENERATION_STEPS, getStepByPhase } from '../constants/generationSteps';
import logging from 'utils/logging';

const log = logging.getLogger('ErrorState');

const ReadyFlag = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.78rem',
  letterSpacing: '0.32em',
  color: '#d04a3a',
  fontWeight: 700,
  textTransform: 'uppercase',
  textAlign: 'center',
  marginBottom: 4,
});

const Title = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '2.4rem',
  color: '#222',
  textAlign: 'center',
  lineHeight: 1.1,
  marginBottom: 8,
});

const Subhead = styled(Typography)({
  fontSize: '1rem',
  color: '#555',
  textAlign: 'center',
  marginBottom: 18,
  maxWidth: 520,
  marginLeft: 'auto',
  marginRight: 'auto',
});

const StoppedAt = styled(Box)({
  margin: '20px 0 18px',
  padding: '14px 16px',
  background: 'rgba(208,74,58,0.06)',
  borderLeft: '3px solid #d04a3a',
  borderRadius: 6,
});

const StoppedLabel = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.65rem',
  letterSpacing: '0.32em',
  color: '#d04a3a',
  textTransform: 'uppercase',
  fontWeight: 700,
});

const StoppedPhase = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '1.2rem',
  color: '#222',
  lineHeight: 1.1,
  marginTop: 4,
});

const PrimaryButton = styled(Button)({
  display: 'block',
  width: '100%',
  maxWidth: 360,
  margin: '0 auto',
  padding: '16px 24px',
  background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
  color: '#fff',
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '1.05rem',
  letterSpacing: '0.18em',
  fontWeight: 800,
  textTransform: 'uppercase',
  borderRadius: 14,
  boxShadow: '0 8px 20px rgba(46,196,182,0.35)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 28px rgba(46,196,182,0.45)',
  },
});

const SecondaryRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: 28,
  marginTop: 18,
  flexWrap: 'wrap',
});

const SecondaryLink = styled(MuiLink)({
  fontSize: '0.85rem',
  color: '#d04a3a',
  borderBottom: '1px solid rgba(208,74,58,0.4)',
  cursor: 'pointer',
  fontWeight: 600,
  textDecoration: 'none',
  paddingBottom: 1,
});

const ErrorState = ({ gameId, gameName, lastCompletedPhase, errorMessage, failedAt, onCancel }) => {
  const [retrying, setRetrying] = useState(false);

  // Determine which phase we'll resume from (next after lastCompleted)
  const nextStep = lastCompletedPhase
    ? GENERATION_STEPS[Math.min(getStepByPhase(lastCompletedPhase).stepNumber, GENERATION_STEPS.length - 1)]
    : GENERATION_STEPS[1]; // after initializing

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await Provider.retryGameGeneration(gameId);
    } catch (err) {
      log.error('Retry failed', err);
      setRetrying(false);
    }
  };

  return (
    <Box>
      <ReadyFlag>Generation Failed</ReadyFlag>
      <Title>{gameName}</Title>
      <Subhead>
        We hit a snag while building your game. You can pick up right where we left off — no work lost.
      </Subhead>

      <StoppedAt>
        <StoppedLabel>Stopped At</StoppedLabel>
        <StoppedPhase>{nextStep.label}</StoppedPhase>
      </StoppedAt>

      <PrimaryButton onClick={handleRetry} disabled={retrying}>
        {retrying ? 'Retrying…' : 'Retry From Here'}
      </PrimaryButton>

      <SecondaryRow>
        <SecondaryLink onClick={onCancel}>Cancel and start over</SecondaryLink>
      </SecondaryRow>

      <WhatWentWrong errorMessage={errorMessage} failedAt={failedAt} failedPhase={nextStep.key} />
    </Box>
  );
};

export default ErrorState;
